import React, { useState, useEffect } from 'react';
import { FiShield, FiUpload, FiCheck, FiX, FiClock, FiAward } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import VerifiedBadge from '../../components/merchant/VerifiedBadge';

const DOC_TYPES = [
  { key: 'id_proof', label: 'ID Proof', desc: 'Government-issued photo ID (Passport, Driving License, Aadhaar)', icon: '🪪' },
  { key: 'address_proof', label: 'Address Proof', desc: 'Utility bill, bank statement, or rental agreement', icon: '🏠' },
  { key: 'business_license', label: 'Business License', desc: 'Business registration certificate or trade license', icon: '📋' },
  { key: 'tax_certificate', label: 'Tax Certificate', desc: 'GST certificate, TIN, or tax registration', icon: '📄' },
];

const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', color: '#D97706', icon: FiClock },
  approved: { bg: '#D1FAE5', color: '#059669', icon: FiCheck },
  rejected: { bg: '#FEE2E2', color: '#EF4444', icon: FiX },
};

const MerchantVerificationPage = () => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [docUrls, setDocUrls] = useState({});

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/verification/status');
      setVerification(res.data.data);
    } catch { /* no store yet */ }
    setLoading(false);
  };

  const handleUrlChange = (type, url) => {
    setDocUrls(prev => ({ ...prev, [type]: url }));
  };

  const handleSubmit = async () => {
    const documents = Object.entries(docUrls)
      .filter(([_, url]) => url?.trim())
      .map(([type, url]) => ({ type, url: url.trim() }));

    if (documents.length === 0) {
      toast.error('Please provide at least one document URL');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/verification/submit', { documents });
      setVerification(res.data.data);
      toast.success('Documents submitted for review!');
      setDocUrls({});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="text-center py-5">Loading...</div>;

  const status = verification?.status || 'unverified';
  const existingDocs = verification?.documents || [];

  return (
    <div>
      <h4 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <FiShield color="#059669" /> Seller Verification
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
        Get verified to earn trust badges on your store and products.
      </p>

      {/* Current Status */}
      <div className="dashboard-card mb-4 p-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h6 style={{ fontWeight: 700, marginBottom: '8px' }}>Verification Status</h6>
            <div className="d-flex align-items-center gap-2">
              <span style={{
                padding: '4px 12px', borderRadius: '20px', fontWeight: 700, fontSize: '13px',
                background: status === 'verified' ? '#D1FAE5' : status === 'pending' ? '#FEF3C7' : status === 'rejected' ? '#FEE2E2' : '#F3F4F6',
                color: status === 'verified' ? '#059669' : status === 'pending' ? '#D97706' : status === 'rejected' ? '#EF4444' : '#6B7280',
              }}>
                {status === 'verified' ? '✅ Verified' : status === 'pending' ? '⏳ Pending Review' : status === 'rejected' ? '❌ Rejected' : '⚪ Not Verified'}
              </span>
              {verification?.level && verification.level !== 'none' && (
                <span style={{ fontSize: '12px', color: '#6B7280' }}>Level: <strong>{verification.level}</strong></span>
              )}
            </div>
            {status === 'rejected' && verification?.rejectionReason && (
              <p style={{ fontSize: '13px', color: '#EF4444', marginTop: '8px' }}>
                <strong>Reason:</strong> {verification.rejectionReason}
              </p>
            )}
          </div>

          {status === 'verified' && (
            <VerifiedBadge verification={verification} size="md" showBadges />
          )}
        </div>
      </div>

      {/* Existing Documents */}
      {existingDocs.length > 0 && (
        <div className="dashboard-card mb-4 p-4">
          <h6 style={{ fontWeight: 700, marginBottom: '12px' }}>📁 Submitted Documents</h6>
          {existingDocs.map((doc, i) => {
            const docType = DOC_TYPES.find(d => d.key === doc.type);
            const statusCfg = STATUS_COLORS[doc.status] || STATUS_COLORS.pending;
            const StatusIcon = statusCfg.icon;
            return (
              <div key={i} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #F3F4F6' }}>
                <div className="d-flex align-items-center gap-2">
                  <span>{docType?.icon || '📄'}</span>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{docType?.label || doc.type}</span>
                </div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                  background: statusCfg.bg, color: statusCfg.color,
                }}>
                  <StatusIcon size={12} /> {doc.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Documents */}
      {status !== 'verified' && (
        <div className="dashboard-card p-4">
          <h6 style={{ fontWeight: 700, marginBottom: '4px' }}>
            <FiUpload size={16} className="me-1" /> {status === 'rejected' ? 'Resubmit Documents' : 'Submit Documents'}
          </h6>
          <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
            Provide URLs to your uploaded documents (use the file upload feature or host them externally).
          </p>

          {DOC_TYPES.map(doc => (
            <div key={doc.key} className="mb-3">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>
                {doc.icon} {doc.label}
                <span className="text-muted fw-normal ms-2" style={{ fontSize: '11px' }}>{doc.desc}</span>
              </label>
              <input
                type="url" className="form-control form-control-sm"
                placeholder="https://example.com/document.pdf"
                value={docUrls[doc.key] || ''}
                onChange={e => handleUrlChange(doc.key, e.target.value)}
              />
            </div>
          ))}

          <button className="btn btn-danger" onClick={handleSubmit} disabled={submitting}>
            <FiUpload size={14} className="me-1" />
            {submitting ? 'Submitting...' : 'Submit for Verification'}
          </button>
        </div>
      )}

      {/* Badge Info */}
      <div className="dashboard-card mt-4 p-4">
        <h6 style={{ fontWeight: 700, marginBottom: '12px' }}><FiAward size={16} className="me-1" /> Available Badges</h6>
        <div className="row g-2">
          {[
            { badge: '🛡️ Identity Verified', req: 'Submit and pass ID verification' },
            { badge: '🏢 Business Verified', req: 'Submit business registration documents' },
            { badge: '📄 Tax Compliant', req: 'Submit tax registration (GST/TIN)' },
            { badge: '⭐ Top Seller', req: '100+ orders completed' },
            { badge: '🌟 Quality Seller', req: 'Average rating 4.5 or above' },
            { badge: '⏰ Established', req: 'Selling on eBuyWiz for 1+ year' },
          ].map((item, i) => (
            <div key={i} className="col-md-6">
              <div className="d-flex gap-2 p-2" style={{ background: '#FAFAFA', borderRadius: '8px' }}>
                <span style={{ fontSize: '18px' }}>{item.badge.split(' ')[0]}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '13px' }}>{item.badge.split(' ').slice(1).join(' ')}</div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>{item.req}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MerchantVerificationPage;
